import { useState } from "react";

import { find } from "lodash/fp";

import Typography from "@mui/material/Typography";

import PreferredPlatformSelector from "@app/analytics/components/PreferredPlatformSelector";
import useConnectCalendar from "@app/analytics/hooks/calendar/useConnectCalendar";
import useRssLinksSettings from "@app/analytics/hooks/settings/useRssLinksSettings";
import * as Events from "@app/analytics/tracking/events";
import { CALENDAR_PROVIDERS_BY_ID } from "@app/common/constants/providers";
import { toast } from "@app/common/hooks/useToasts";
import ROUTES from "@app/common/routes";
import type { RssLinkPlatformPreference } from "@app/common/types";
import type { CalendarProviderId } from "@app/common/types/providerTypes";
import { withQueryParamsUrl } from "@app/common/utils";

import ConfirmationModal from "@rui/ConfirmationModal";
import Dropdown, { DropdownMenuItem } from "@rui/Dropdown";
import { getSelectedFromOptions } from "@rui/Dropdown/utils";
import Stack from "@rui/Stack";

const ConfigureRSSLinksModal = ({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) => {
  const [enabling, setEnabling] = useState(false);

  const { connectCalendar } = useConnectCalendar();
  const {
    connectedRssCalendarProviders,
    rssCalendar,
    rssLinkSettings,
    updateRssLinkSettings,
  } = useRssLinksSettings();

  const rssCalendarProvider = rssCalendar
    ? find({ providerId: rssCalendar.provider }, connectedRssCalendarProviders)
    : null;

  const handleUpdateDefaultCalendar = (providerId: CalendarProviderId) => {
    if (providerId === rssCalendarProvider?.providerId) return;
    updateRssLinkSettings.mutate({ calendarProvider: providerId });
  };

  const handleChangePlatform = (
    platformPreference: RssLinkPlatformPreference
  ) => {
    updateRssLinkSettings.mutate({ platformPreference });
  };

  const handleConnectCalendar = (providerId: CalendarProviderId) =>
    connectCalendar(providerId, {
      returnTo: withQueryParamsUrl(ROUTES.CALENDAR_SCHEDULING, {
        configuring: true,
      }),
    });

  const handleEnableRssLinks = () => {
    Events.enableRssLinks();
    setEnabling(true);

    updateRssLinkSettings.mutate(
      { isEnabled: true },
      {
        onSettled: () => {
          setEnabling(false);
          onClose();
        },
        onError: () => {
          toast.error(
            "Something went wrong and Smart Scheduler Links could not be enabled. Please try again or reach out to support@read.ai if the problem persists."
          );
        },
      }
    );
  };

  const dropdownOptions = connectedRssCalendarProviders.map((provider) => ({
    value: provider.providerId,
    label: CALENDAR_PROVIDERS_BY_ID[provider.providerId].displayName,
  }));

  return (
    <ConfirmationModal
      open={open}
      title="Configure Smart Scheduler Links"
      onConfirm={handleEnableRssLinks}
      onClose={onClose}
      size="md"
      loading={enabling}
      confirmProps={{
        disabled: !rssCalendar,
        tooltip: "Select a default calendar to proceed",
      }}
    >
      <Stack spacing={2}>
        <Stack spacing="2px">
          <Typography variant="bodyPrimaryMed" color="shades.neutral.700">
            Select default calendar
          </Typography>
          <Typography variant="bodySecondaryReg" color="shades.neutral.500">
            This calendar will be used to find availability
          </Typography>
        </Stack>
        <Dropdown
          name="rss-links-default-calendar-id"
          selectedItem={
            rssCalendarProvider
              ? getSelectedFromOptions(
                  dropdownOptions,
                  rssCalendarProvider.providerId
                )
              : undefined
          }
          onChange={(event) =>
            handleUpdateDefaultCalendar(
              event.target.value as CalendarProviderId
            )
          }
          placeholder="Select a default calendar"
        >
          {dropdownOptions.map(({ value, label }) => (
            <DropdownMenuItem key={value} value={value} label={label} />
          ))}
        </Dropdown>

        <Stack spacing="2px">
          <Typography variant="bodyPrimaryMed" color="shades.neutral.700">
            Default conferencing platform
          </Typography>
          <Typography variant="bodySecondaryReg" color="shades.neutral.500">
            Meetings scheduled with Smart Scheduler will use this platform by
            default
          </Typography>
        </Stack>
        <PreferredPlatformSelector
          onChange={handleChangePlatform}
          onConnectCalendar={handleConnectCalendar}
          platformOptions={rssLinkSettings.platformOptions}
          selected={rssLinkSettings.platformPreference}
        />
      </Stack>
    </ConfirmationModal>
  );
};

export default ConfigureRSSLinksModal;
